import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  DocumentIcon,
  EnvelopeIcon,
  GiftIcon,
  GlobeAltIcon,
  InformationCircleIcon,
  LightBulbIcon,
  LinkIcon,
  SparklesIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import DashboardLayout from "../../components/DashboardLayout";
import { useToast } from "../../contexts/ToastContext";
import { giftsService } from "../../services/gifts";
import { projectPlansService } from "../../services/projectPlans";
import { getApiErrorMessage } from "../../utils/axios";
import { timeStampToEasyTime } from "../../utils/time";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INTRO_STEPS = [
  {
    title: "Send an invite",
    subtitle: "They get to skip the waitlist and try Weave today",
    icon: EnvelopeIcon,
  },
  {
    title: "Gift a site",
    subtitle: "Optionally include a site you've built that they would love",
    icon: GiftIcon,
  },
  {
    title: "They start weaving",
    subtitle: "They sign up, claim the gift, and start building.",
    icon: SparklesIcon,
  },
];

// Keys match payments.constants.GIFT_REASON_*.
const EARN_CRITERIA = [
  { id: "published_site", label: "Publish your first site" },
  { id: "edited_site", label: "Make an edit request" },
  { id: "custom_domain", label: "Link a custom domain" },
  { id: "clean_seo_audit", label: "Pass a clean SEO audit" },
  { id: "clean_ux_audit", label: "Pass a clean UX audit" },
  { id: "clean_page_speed", label: "Pass a clean page speed audit" },
];

const GIFT_TYPE_OPTIONS = [
  {
    id: "empty",
    title: "Blank Slate",
    subtitle: "They get to build a site from scratch",
    icon: DocumentIcon,
  },
  {
    id: "site_plan",
    title: "Site Idea",
    subtitle: "They take one of your plans and riff on it",
    icon: LightBulbIcon,
  },
  {
    id: "site",
    title: "Built Site",
    subtitle: "Gift a site you built and make a new one instead",
    icon: GlobeAltIcon,
  },
];

const planLabel = (plan) => {
  if (!plan) return "Untitled plan";
  return (
    (plan.site_plan && plan.site_plan.name) ||
    plan.subdomain ||
    (plan.hash ? `Plan ${plan.hash.slice(0, 8)}` : "Untitled plan")
  );
};

const Gifts = ({ currentUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { error: toastError, success: toastSuccess } = useToast();
  const projects = useSelector((state) =>
    state.project.projects.filter((project) => !project.is_archived),
  );
  const [gifts, setGifts] = useState([]);
  const [invitesEarned, setInvitesEarned] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);

  const sendId = (location.pathname.match(/\/invites\/(\d+)\/send\/?$/) || [])[1];
  const available = gifts.filter((g) => g.available);
  const sent = gifts
    .filter((g) => !g.available)
    .sort((a, b) => (b.sent_at || "").localeCompare(a.sent_at || ""));
  const nextGift = available[0] || null;
  const sendingGift = available.find((g) => String(g.id) === sendId) || null;
  const availableCount = available.length;

  const reload = () => {
    giftsService.list(
      (res) => {
        setGifts(res.data.gifts);
        setInvitesEarned(res.data.invites_earned);
      },
      () => {
        setGifts([]);
        setInvitesEarned({});
      },
    );
  };

  useEffect(() => {
    reload();
    projectPlansService.list(
      (res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
        setPlans(list);
      },
      () => setPlans([]),
    );
  }, []);

  useEffect(() => {
    if (sendId && gifts.length && !sendingGift) {
      navigate("/invites/", { replace: true });
    }
  }, [sendId, gifts.length, sendingGift, navigate]);

  const send = (data) => {
    if (!sendingGift) return;
    setBusy(true);
    giftsService.send(
      sendingGift.id,
      {
        email: data.email,
        gift_type: data.gift_type,
        project_plan: data.project_plan || undefined,
        project: data.project || undefined,
        message: data.message,
      },
      () => {
        toastSuccess("Invite sent");
        setBusy(false);
        reload();
        navigate("/invites/");
      },
      (err) => {
        setBusy(false);
        toastError(getApiErrorMessage(err, "Could not send invite"));
      },
    );
  };

  return (
    <DashboardLayout
      title="Invites"
      currentUser={currentUser}
      primaryAction={(
        <button
          type="button"
          disabled={availableCount === 0}
          onClick={() => navigate(`/invites/${nextGift.id}/send/`)}
          className="rounded-lg bg-theme-primary px-3 py-1.5 text-xs font-semibold text-theme-button disabled:opacity-50 lg:px-4 lg:py-2 lg:text-sm"
        >
          Send new invite
        </button>
      )}
    >
      <div className="flex flex-col gap-10 pt-2 lg:flex-row lg:gap-8 lg:pt-4">
        <div className="w-full space-y-10 lg:w-3/4">
          <section>
            <GiftIcon className="h-10 w-10 text-theme-primary" />
            <h2 className="mt-4 text-lg font-semibold text-theme-text">
              Invite someone to Weave
            </h2>
            <p className="mt-2 max-w-md text-sm leading-6 text-theme-text opacity-60">
              Weave is invite-only right now. Send an invite to someone you think
              would love it, and optionally gift them a site to start with.
            </p>
            <div className="mt-8 grid max-w-2xl gap-6 sm:grid-cols-3">
              {INTRO_STEPS.map((step) => (
                <div key={step.title}>
                  <step.icon className="h-5 w-5 text-theme-text opacity-70" />
                  <div className="mt-2 text-sm font-medium text-theme-text">{step.title}</div>
                  <p className="mt-0.5 text-xs leading-5 text-theme-text opacity-60">
                    {step.subtitle}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-theme-secondary-text">
              Earn invites
            </h2>
            <ul className="space-y-1">
              {EARN_CRITERIA.map((criterion) => {
                const earned = invitesEarned?.[criterion.id];
                const gift = gifts.find((g) => g.reason === criterion.id);
                return (
                  <li key={criterion.id} className="flex h-10 items-center gap-3">
                    {!invitesEarned ? (
                      <ArrowPathIcon className="h-5 w-5 flex-shrink-0 animate-spin text-theme-text opacity-30" />
                    ) : earned ? (
                      <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
                    ) : (
                      <XCircleIcon className="h-5 w-5 flex-shrink-0 text-theme-text opacity-30" />
                    )}
                    <span className={`min-w-0 flex-1 truncate text-sm text-theme-text ${earned ? "" : "opacity-60"}`}>
                      {criterion.label}
                    </span>
                    {gift && gift.available ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/invites/${gift.id}/send/`)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-theme-secondary px-3 py-1 text-xs font-semibold text-theme-text hover:opacity-80"
                      >
                        <GiftIcon className="h-3.5 w-3.5" />
                        Send
                      </button>
                    ) : gift ? (
                      <span className="inline-flex items-center gap-1.5 px-3 text-xs text-theme-text opacity-60">
                        <CheckCircleIcon className="h-3.5 w-3.5 text-green-500" />
                        Sent
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
        </div>

        <aside className="w-full space-y-8 lg:w-1/4">
          <GiftGauge sent={sent.length} total={gifts.length} />

          <div>
            <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-theme-secondary-text">
              Sent invites
            </h2>
            {sent.length === 0 ? (
              <p className="text-xs text-theme-text opacity-50">No invites sent yet.</p>
            ) : (
              <ul className="space-y-2">
                {sent.map((row) => (
                  <li key={row.id} className="flex items-center gap-2.5">
                    {row.accepted ? (
                      <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-500" title="Accepted" />
                    ) : (
                      <EnvelopeIcon className="h-4 w-4 flex-shrink-0 text-theme-text opacity-50" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium text-theme-text" title={row.email || ""}>
                        {row.email || "Unknown"}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-theme-text opacity-50">
                        <span className="flex-shrink-0">
                          {row.sent_at ? timeStampToEasyTime(Date.parse(row.sent_at) / 1000) : ""}
                        </span>
                        <span className="ml-auto min-w-0 truncate text-right">
                          {row.gift_type === "empty" ? (
                            "Invite"
                          ) : row.gift_type === "site" && row.site_url ? (
                            <a
                              href={row.site_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex max-w-full items-center gap-1 hover:text-theme-primary"
                            >
                              <span className="truncate">{row.name}</span>
                              <ArrowTopRightOnSquareIcon className="h-3 w-3 flex-shrink-0" />
                            </a>
                          ) : (
                            row.name
                          )}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>

      {sendingGift && (
        <GiftModal
          plans={plans}
          projects={projects}
          busy={busy}
          onClose={() => navigate("/invites/")}
          onSend={send}
        />
      )}
    </DashboardLayout>
  );
};

const GiftModal = ({ plans, projects, busy, onClose, onSend }) => {
  const [email, setEmail] = useState("");
  const [giftType, setGiftType] = useState("");
  const [projectPlan, setProjectPlan] = useState("");
  const [project, setProject] = useState("");
  const [message, setMessage] = useState("");
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  const validateEmail = (value) => {
    const trimmed = value.trim().toLowerCase();
    if (!trimmed) return "Email is required";
    if (!EMAIL_RE.test(trimmed)) return "Enter a valid email address";
    return "";
  };

  const canSubmit =
    !busy &&
    !validateEmail(email) &&
    giftType &&
    (giftType !== "site_plan" || projectPlan) &&
    (giftType !== "site" || project);

  const selectGiftType = (id) => {
    setGiftType(id);
    if (id !== "site_plan") setProjectPlan("");
    if (id !== "site") setProject("");
  };

  const submit = (event) => {
    event.preventDefault();
    const err = validateEmail(email);
    setEmailError(err);
    if (err || !canSubmit) return;
    onSend({
      email: email.trim().toLowerCase(),
      gift_type: giftType,
      project_plan: projectPlan,
      project,
      message: message.trim(),
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:items-center"
      onClick={() => !busy && onClose()}
    >
      <div
        className="relative my-auto w-full max-w-lg rounded-xl border border-theme-secondary bg-theme-main"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-theme-secondary px-4 py-3">
          <h2 className="text-sm font-semibold text-theme-text">Invite a Friend / Gift a Site</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded p-1 opacity-60 hover:opacity-100 disabled:opacity-40"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <form className="space-y-4 p-4" onSubmit={submit}>
          <p className="text-xs leading-5 text-theme-text opacity-60">
            They must be new to Weave. We&apos;ll email them an invite to register
            and claim the gift.
          </p>
          <label className="block text-xs font-medium text-theme-text">
            Recipient email
            <input
              type="email"
              autoFocus
              autoComplete="email"
              value={email}
              disabled={busy}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(validateEmail(e.target.value));
              }}
              onBlur={() => setEmailError(validateEmail(email))}
              className="mt-1 block w-full rounded-lg border border-theme-secondary bg-transparent px-3 py-2 text-sm disabled:opacity-60"
              placeholder="friend@example.com"
            />
            {emailError ? (
              <span className="mt-1 block text-[11px] text-red-600">{emailError}</span>
            ) : null}
          </label>

          <div>
            <div className="mb-2 text-xs font-medium text-theme-text">What to gift</div>
            <div className="grid gap-2 sm:grid-cols-3">
              {GIFT_TYPE_OPTIONS.map((option) => {
                const Icon = option.icon;
                const selected = giftType === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={busy}
                    onClick={() => selectGiftType(option.id)}
                    className={`relative rounded-lg border-2 p-3 text-left transition-all disabled:opacity-60 ${
                      selected
                        ? "border-theme-primary"
                        : "border-theme-secondary hover:border-theme-primary hover:border-opacity-50"
                    }`}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <Icon className="h-5 w-5 text-theme-text opacity-70" />
                      {selected && (
                        <CheckCircleIcon className="h-5 w-5 text-theme-primary" />
                      )}
                    </div>
                    <div className="text-sm font-medium text-theme-text">{option.title}</div>
                    <p className="mt-0.5 text-[11px] leading-4 text-theme-text opacity-60">
                      {option.subtitle}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {giftType === "site_plan" && (
            <div>
              <div className="mb-1.5 text-xs font-medium text-theme-text">Site plan</div>
              <PickerDropdown
                disabled={busy}
                placeholder="Select a plan…"
                emptyIcon={LightBulbIcon}
                valueLabel={
                  projectPlan
                    ? planLabel(plans.find((p) => p.hash === projectPlan) || {})
                    : null
                }
                emptyLabel="No site plans yet"
                items={plans.map((p) => ({
                  id: p.hash,
                  label: planLabel(p),
                  icon: <LightBulbIcon className="h-4 w-4 flex-shrink-0 opacity-50" />,
                  action: p.site_plan?.description ? (
                    <span title={p.site_plan.description} className="cursor-help p-1 opacity-50 hover:opacity-100">
                      <InformationCircleIcon className="h-4 w-4" />
                    </span>
                  ) : null,
                }))}
                selectedId={projectPlan}
                onSelect={setProjectPlan}
              />
            </div>
          )}

          {giftType === "site" && (
            <div>
              <div className="mb-1.5 text-xs font-medium text-theme-text">Site to transfer</div>
              <PickerDropdown
                disabled={busy}
                placeholder="Select a site…"
                emptyIcon={GlobeAltIcon}
                valueLabel={
                  project
                    ? (projects.find((p) => p.hash === project) || {}).name
                    : null
                }
                emptyLabel="No sites yet"
                items={projects.map((p) => ({
                  id: p.hash,
                  label: p.name || "Unnamed site",
                  icon: <GlobeAltIcon className="h-4 w-4 flex-shrink-0 opacity-50" />,
                  action: p.production_url ? (
                    <a
                      href={p.production_url}
                      target="_blank"
                      rel="noreferrer"
                      title="Open live site"
                      className="p-1 opacity-50 hover:text-theme-primary hover:opacity-100"
                    >
                      <LinkIcon className="h-4 w-4" />
                    </a>
                  ) : null,
                }))}
                selectedId={project}
                onSelect={setProject}
              />
            </div>
          )}

          {giftType && (
            <label className="block text-xs font-medium text-theme-text">
              Message (optional)
              <textarea
                rows={3}
                value={message}
                disabled={busy}
                onChange={(e) => setMessage(e.target.value)}
                className="mt-1 block w-full resize-none rounded-lg border border-theme-secondary bg-transparent px-3 py-2 text-sm font-normal disabled:opacity-60"
                placeholder="Tell your friend why you think they'd like Weave, or what you'd love to see them build"
              />
            </label>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-lg border border-theme-secondary px-3 py-2 text-xs font-semibold disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-lg bg-theme-primary px-3 py-2 text-xs font-semibold text-theme-button disabled:opacity-50"
            >
              {busy ? "Sending…" : "Send gift"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

// Sidebar-style picker used for site / site plan selection in the gift modal.
const PickerDropdown = ({
  disabled,
  placeholder,
  emptyIcon: EmptyIcon = GlobeAltIcon,
  valueLabel,
  emptyLabel,
  items,
  selectedId,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`group flex w-full items-center rounded-lg border border-theme-secondary px-2.5 py-2 text-left text-sm text-theme-text transition hover:bg-black hover:bg-opacity-5 disabled:opacity-60 ${
          valueLabel ? "bg-black bg-opacity-5" : ""
        }`}
      >
        {selectedId ? (
          <span className="h-4 w-4 flex-shrink-0 rounded bg-theme-primary" />
        ) : (
          <EmptyIcon className="h-4 w-4 flex-shrink-0 opacity-50" />
        )}
        <span className="ml-2 min-w-0 flex-1 truncate text-[13px] font-semibold">
          {valueLabel || placeholder}
        </span>
        <svg
          className={`h-4 w-4 flex-shrink-0 opacity-60 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-lg border border-theme-secondary bg-theme-main shadow-lg">
          <div className="max-h-64 overflow-y-auto py-1">
            {items.map((item) => (
              <div
                key={item.id}
                className={`flex items-center pr-2 text-sm text-theme-text transition hover:bg-black hover:bg-opacity-5 ${
                  selectedId === item.id ? "bg-black bg-opacity-5" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    onSelect(item.id);
                    setOpen(false);
                  }}
                  className={`flex min-w-0 flex-1 items-center gap-2 px-3 py-2 hover:text-theme-primary ${
                    selectedId === item.id ? "font-semibold text-theme-primary" : ""
                  }`}
                >
                  {item.icon}
                  <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
                </button>
                {item.action}
              </div>
            ))}
            {!items.length ? (
              <p className="px-3 py-2 text-sm text-theme-text opacity-60">{emptyLabel}</p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

// Half donut showing sent / total invites; fills in on mount via a CSS transition.
const GiftGauge = ({ sent, total }) => {
  const [filled, setFilled] = useState(0);
  const pct = total ? (sent / total) * 100 : 0;

  useEffect(() => {
    const id = requestAnimationFrame(() => setFilled(pct));
    return () => cancelAnimationFrame(id);
  }, [pct]);

  return (
    <div className="relative mx-auto w-full max-w-[180px]">
      <svg viewBox="0 0 120 66" className="w-full">
        <path
          d="M 10 60 A 50 50 0 0 1 110 60"
          fill="none"
          stroke="var(--theme-secondary-bg)"
          strokeWidth="10"
          strokeLinecap="round"
        />
        <path
          d="M 10 60 A 50 50 0 0 1 110 60"
          fill="none"
          stroke="var(--theme-primary)"
          strokeWidth="10"
          strokeLinecap="round"
          pathLength="100"
          strokeDasharray="100"
          strokeDashoffset={100 - filled}
          style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.22, 1, 0.36, 1)" }}
          opacity={filled ? 1 : 0}
        />
      </svg>
      <div className="absolute inset-x-0 bottom-0 text-center">
        <div className="text-2xl font-semibold text-theme-text">
          {total ? `${sent}/${total}` : "0"}
        </div>
        <div className="text-[11px] text-theme-text opacity-50">invites sent</div>
      </div>
    </div>
  );
};

export default Gifts;
